import config from '../config';
import ApiService from './ApiService';
import * as XLSX from 'xlsx';

/**
 * Converts a params object to a URL query string, handling arrays.
 */
function buildQueryString(params = {}) {
  const parts = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined && v !== '') {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
        }
      });
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

async function fetchJson(endpoint) {
  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    headers: ApiService.getDefaultHeaders(),
  });
  if (!response.ok) {
    throw new Error(`API Error ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

class DashboardAnalyticsService {
  /**
   * GET /api/dashboard/analytics/summary
   * params: { odm_type, year, start_date, end_date }
   */
  static getSummary(params = {}) {
    return fetchJson(`/api/dashboard/analytics/summary${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/filter-options
   */
  static getFilterOptions() {
    return fetchJson('/api/dashboard/analytics/filter-options');
  }

  /**
   * GET /api/dashboard/analytics/volume-by-request-status
   * params: { year, quarter, odm_type, start_date, end_date }
   */
  static getVolumeByRequestStatus(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-request-status${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/volume-by-approval-status
   * params: { year, quarter, odm_type, start_date, end_date }
   */
  static getVolumeByApprovalStatus(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-approval-status${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/volume-by-type
   * params: { years: [], include_wip, start_date, end_date }
   */
  static getVolumeByType(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-type${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/wip-summary
   * params: { odm_type, year, start_date, end_date }
   */
  static getWipSummary(params = {}) {
    return fetchJson(`/api/dashboard/analytics/wip-summary${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/wip-totals
   * params: { odm_type, years: [] }
   */
  static getWipTotals(params = {}) {
    return fetchJson(`/api/dashboard/analytics/wip-totals${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/wip-by-phase
   * params: { year, odm_type, start_date, end_date }
   */
  static getWipByPhase(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/wip-by-phase${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/data-view
   * params: { view_type, odm_type, year, start_date, end_date, page, page_size }
   */
  static getDataView(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/data-view${buildQueryString(params)}`
    );
  }

  /**
   * Triggers a browser download for data as CSV or XLSX.
   * @param {Array} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {'csv'|'xlsx'} format - Export format
   */
  static downloadExport(data, filename, format = 'csv') {
    if (!data || data.length === 0) return;

    if (format === 'xlsx') {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    } else {
      // CSV
      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map((row) =>
          headers
            .map((h) => {
              const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
              // Escape values containing commas, quotes, or newlines
              if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                return `"${val.replace(/"/g, '""')}"`;
              }
              return val;
            })
            .join(',')
        ),
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}

export default DashboardAnalyticsService;
